import React, { useEffect } from "react";
import { Navigate, useLocation, createBrowserRouter } from "react-router-dom";

import {
  DrawerLayout,
  MainSideNav,
  FooterPublic,
  AuthLayout,
} from "./components";
import Login from "./auth/Login";
import {
  Dashboard,
  Indicators,
  Support,
  AccountSettings,
  PeopleHome,
  PeopleList,
  PersonNew,
  PublicPolicies,
  PoliciesList,
  PolicyDetails,
  PolicyEdit,
  PolicyNew,
  PracticeDetails,
  PracticeEdit,
  PracticeNew,
  PublicPractices,
  PracticesList,
  LandingPage,
} from "./pages";
import {
  PrivatePractices,
  PrivatePracticesList,
  PrivatePolicies,
  PrivatePoliciesList,
} from "./pages/students";
import { PersonDetail } from "./pages";
import {
  AdminPeople,
  AdminPolicies,
  AdminPractices,
  AdminPendingSubmissionALL,
  AdminDetailsView,
  AdminViewAll,
  IndicatorValues,
} from "./pages/admin/data";

import {
  Country,
  Themes,
  GeographicalArea,
  PolicyCategories,
  PracticeCategories,
  Institutions,
  Role,
  // AdminIndicators,
} from "./pages/admin/parameters";
import { AddFeatures, UpdateFeatures } from "./pages/admin";
import { AdminIndicators } from "./pages/admin/parameters/indicators";

import useAuthSession from "./components/AuthSessionProvider/useAuthSession";
import { userRoleType } from "./utils/constants";
import { AllReports } from "./features";
import { useTokenExpiryHandler } from "./hooks";
import { AllSubmissions } from "./pages/dashboard/allSubmissions";
import { Users, Profiles } from "./pages/admin/accounts";
import { IndicatorsCategories } from "./pages/admin/parameters/indicatorsCategories";
import { TermsAndConditions } from "./pages/termsAndConditions";
import { PrivacyPolicy } from "./pages/privacyPolicy";
import { ForgotPassword } from "./pages/forgotPassword";
import { OtpVerification } from "./pages/forgotPassword/OtpVerification";
import { NewPassword } from "./pages/forgotPassword/newPassword";

type AuthGuardProps = {
  policy:
    | "require_auth"
    | "require_no_auth"
    | "require_admin_auth"
    | "require_both_auth"
    | "require_password_token";
  redirectTo: string;
  children?: React.ReactNode;
};

const AuthGuard: React.FC<AuthGuardProps> = (props) => {
  const { user, passwordToken } = useAuthSession() as any;

  useTokenExpiryHandler(user?.expiry);

  const isAuthenticated = !!user;
  switch (props.policy) {
    case "require_both_auth": {
      if (isAuthenticated) return <>{props.children}</>;
      else return <Navigate to={props.redirectTo} />;
    }
    case "require_auth": {
      if (user?.profile?.name === userRoleType?.student)
        return <>{props.children}</>;
      else return <Navigate to={props.redirectTo} />;
    }
    case "require_admin_auth": {
      if (user?.profile?.name === userRoleType?.admin)
        return <>{props.children}</>;
      else return <Navigate to={props.redirectTo} />;
    }
    case "require_no_auth": {
      if (!isAuthenticated) return <>{props.children}</>;
      else return <Navigate to={props.redirectTo} />;
    }
    case "require_password_token": {
      if (passwordToken) return <>{props.children}</>;
      else return <Navigate to={props.redirectTo} />;
    }
    default: {
      return <>{props.children}</>;
    }
  }
};

function RefreshableIndicators() {
  const location = useLocation();

  return <Indicators key={`${location.pathname}${location.search}`} />;
}

const ScrollToTop: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return <>{children}</>;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ScrollToTop>
        <LandingPage />
      </ScrollToTop>
    ),
  },
  {
    path: "",
    element: (
      <DrawerLayout
        drawerContent={<MainSideNav />}
        footerContent={<FooterPublic />}
      />
    ),
    children: [
      {
        path: "dashboard",
        element: (
          <ScrollToTop>
            <Dashboard />
          </ScrollToTop>
        ),
      },
      { path: "indicators", element: <RefreshableIndicators /> },
      { path: "terms-conditions", element: <TermsAndConditions /> },
      { path: "privacy-policy", element: <PrivacyPolicy /> },
      {
        path: "policies",
        element: (
          <ScrollToTop>
            <PublicPolicies />
          </ScrollToTop>
        ),
      },
      {
        path: "policies/all",
        element: (
          <ScrollToTop>
            <PoliciesList />
          </ScrollToTop>
        ),
      },
      {
        path: ":module/view-all-Reports",
        element: (
          <ScrollToTop>
            <AllReports />
          </ScrollToTop>
        ),
      },
      {
        path: "policies/:policyId",
        element: (
          <ScrollToTop>
            <PolicyDetails />
          </ScrollToTop>
        ),
      },

      {
        path: "practices",
        element: (
          <ScrollToTop>
            <PublicPractices />{" "}
          </ScrollToTop>
        ),
      },
      {
        path: "practices/all",
        element: (
          <ScrollToTop>
            <PracticesList />
          </ScrollToTop>
        ),
      },
      {
        path: "practices/:practiceId",
        element: (
          <ScrollToTop>
            <PracticeDetails />
          </ScrollToTop>
        ),
      },

      {
        path: "people",
        element: (
          <ScrollToTop>
            <PeopleHome />
          </ScrollToTop>
        ),
      },
      {
        path: "personDetail",
        element: (
          <ScrollToTop>
            {" "}
            <PersonDetail />
          </ScrollToTop>
        ),
      },
      {
        path: "people/all",
        element: (
          <ScrollToTop>
            <PeopleList />
          </ScrollToTop>
        ),
      },

      {
        path: "support",
        element: (
          <ScrollToTop>
            <Support />
          </ScrollToTop>
        ),
      },

      // protected nested route
      {
        path: "admin/:basePath/all",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <AdminViewAll />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/:basePath/view_all",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <AdminPendingSubmissionALL />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/:basePath/:Id",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <AdminDetailsView />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/person_detail",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PersonDetail />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/practices",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <AdminPractices />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/policies",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <AdminPolicies />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/people",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <AdminPeople />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/indicators-values",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <IndicatorValues />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/indicators",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <AdminIndicators />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/users",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <Users />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/profiles",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <Profiles />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/country",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <Country />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/themes",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <Themes />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/geographical-area",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <GeographicalArea />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/policy-categories",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PolicyCategories />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/practice-categories",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PracticeCategories />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/indicators-categories",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <IndicatorsCategories />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/institutions",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <Institutions />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/role",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <Role />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/:basePath/add-new",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <AddFeatures />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/:basePath/update",
        element: (
          <AuthGuard policy="require_admin_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <UpdateFeatures />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "submission/dashboard",
        element: (
          <AuthGuard policy="require_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <Dashboard />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "submissions/dashboard/all",
        element: (
          <AuthGuard policy="require_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <AllSubmissions />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: ":module/practices/:practiceId/edit",
        element: (
          <AuthGuard policy="require_both_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PracticeEdit />
            </ScrollToTop>
          </AuthGuard>
        ),
      },

      {
        path: "account-settings",
        element: (
          <AuthGuard policy="require_both_auth" redirectTo="/auth/login">
            <AccountSettings />
          </AuthGuard>
        ),
      },
      {
        path: "submission/people",
        element: (
          <AuthGuard policy="require_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PeopleHome />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: ":basePath/people/:module",
        element: (
          <AuthGuard policy="require_both_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PersonNew />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: ":module/people/:peopleId/edit",
        element: (
          <AuthGuard policy="require_both_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PersonNew />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "admin/people/new",
        element: (
          <AuthGuard policy="require_both_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PersonNew />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "submission/people/all",
        element: (
          <AuthGuard policy="require_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PeopleList />
            </ScrollToTop>
          </AuthGuard>
        ),
      },

      {
        path: "submission/policies",
        element: (
          <AuthGuard policy="require_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PrivatePolicies />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "submission/policies/all",
        element: (
          <AuthGuard policy="require_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PrivatePoliciesList />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: ":basePath/policies/new",
        element: (
          <AuthGuard policy="require_both_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PolicyNew />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: ":module/policies/:policyId/edit",
        element: (
          <AuthGuard policy="require_both_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PolicyEdit />
            </ScrollToTop>
          </AuthGuard>
        ),
      },

      {
        path: "submission/practices",
        element: (
          <AuthGuard policy="require_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PrivatePractices />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: ":basePath/practices/new",
        element: (
          <AuthGuard policy="require_both_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PracticeNew />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
      {
        path: "submission/practices/all",
        element: (
          <AuthGuard policy="require_auth" redirectTo="/auth/login">
            <ScrollToTop>
              <PrivatePracticesList />
            </ScrollToTop>
          </AuthGuard>
        ),
      },
    ],
  },
  {
    path: "auth",
    element: (
      <AuthGuard policy="require_no_auth" redirectTo="/dashboard">
        <AuthLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to="login" /> },
      { path: "login", element: <Login /> },
      { path: "forgot-password", element: <ForgotPassword /> },
      { path: "otp-verification", element: <OtpVerification /> },
      {
        path: "new-password",
        element: (
          <AuthGuard policy="require_password_token" redirectTo="/auth/login">
            <NewPassword />
          </AuthGuard>
        ),
      },
    ],
  },
]);

export default router;
